/**
 * QEMU Monitor Terminal
 *
 * Provides an xterm.js-based terminal for QEMU HMP commands via WebSocket
 */

import { Terminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import { api } from '../api/client.js';
import { pasteDialog } from '../ui/dialogs.js';

// Monitor WebSocket message types
interface MonitorMessage {
  type: 'connected' | 'response' | 'error';
  vm_name?: string;
  message?: string;
  output?: string;
}

// Module state
let monitorFitAddon: FitAddon | null = null;
let monitorWebSocket: WebSocket | null = null;

/**
 * Build WebSocket URL for monitor console
 */
function buildMonitorWsUrl(vmId: number): string {
  return api.buildWsUrl(`/ws/vm/${vmId}/monitor`);
}

/**
 * Open the QEMU monitor console dialog
 */
export function openMonitorConsole(vmId: number, vmName: string): void {
  const dialogId = 'monitor-console-dialog';

  // Remove existing dialog and close any existing WebSocket
  $(`#${dialogId}`).remove();
  if (monitorWebSocket) {
    monitorWebSocket.close();
    monitorWebSocket = null;
  }

  const html = `
    <div id="${dialogId}">
      <div class="monitor-toolbar">
        <button class="ui-button vnc-btn" id="monitor-paste-btn" onclick="monitorPaste()" title="Paste clipboard contents">
          Paste
        </button>
      </div>
      <div id="monitor-terminal" class="monitor-terminal"></div>
    </div>
  `;

  $(html).appendTo('body').dialog({
    modal: true,
    title: `QEMU Monitor - ${vmName}`,
    width: 800,
    height: 500,
    resizable: true,
    close: function () {
      // Close WebSocket on dialog close
      if (monitorWebSocket) {
        monitorWebSocket.close();
        monitorWebSocket = null;
      }
      monitorFitAddon = null;
      $(this).remove();
    },
    open: function () {
      initMonitorTerminal(vmId);
    },
    resize: function () {
      if (monitorFitAddon) {
        monitorFitAddon.fit();
      }
    },
  });
}

/**
 * Initialize the xterm.js terminal with WebSocket connection
 */
function initMonitorTerminal(vmId: number): void {
  const container = document.getElementById('monitor-terminal');
  if (!container) return;

  // Create terminal
  const term = new Terminal({
    cursorBlink: true,
    fontSize: 14,
    fontFamily: 'Consolas, "Courier New", monospace',
    theme: {
      background: '#1e1e1e',
      foreground: '#d4d4d4',
      cursor: '#ffffff',
      selectionBackground: '#264f78',
      black: '#000000',
      red: '#cd3131',
      green: '#0dbc79',
      yellow: '#e5e510',
      blue: '#2472c8',
      magenta: '#bc3fbc',
      cyan: '#11a8cd',
      white: '#e5e5e5',
    },
  });

  // Fit addon
  monitorFitAddon = new FitAddon();
  term.loadAddon(monitorFitAddon);

  term.open(container);

  if (monitorFitAddon) {
    monitorFitAddon.fit();
  }

  // Terminal state
  let commandBuffer = '';
  let commandHistory: string[] = [];
  let historyIndex = -1;
  let isConnected = false;
  let pendingCommand: string | null = null;

  function writePrompt(): void {
    if (isConnected) {
      term.write('\x1b[1;32m(qemu)\x1b[0m ');
    }
  }

  function clearLine(): void {
    while (commandBuffer.length > 0) {
      term.write('\b \b');
      commandBuffer = commandBuffer.slice(0, -1);
    }
  }

  // Welcome message
  term.writeln('\x1b[1;36m╔═══════════════════════════════════════════════════════════╗\x1b[0m');
  term.writeln('\x1b[1;36m║\x1b[0m        \x1b[1;33mQEMU Human Monitor Protocol (HMP)\x1b[0m               \x1b[1;36m║\x1b[0m');
  term.writeln('\x1b[1;36m╚═══════════════════════════════════════════════════════════╝\x1b[0m');
  term.writeln('');
  term.writeln('\x1b[90mConnecting to QEMU monitor...\x1b[0m');

  const wsUrl = buildMonitorWsUrl(vmId);
  monitorWebSocket = new WebSocket(wsUrl);

  monitorWebSocket.onopen = () => {
    // Connection established, wait for 'connected' message from server
  };

  monitorWebSocket.onmessage = (event) => {
    try {
      const msg: MonitorMessage = JSON.parse(event.data);

      switch (msg.type) {
        case 'connected':
          isConnected = true;
          term.writeln('\x1b[1;32mConnected!\x1b[0m');
          term.writeln('');
          term.writeln('\x1b[90mType "help" for available commands, "info" for VM information.\x1b[0m');
          term.writeln('\x1b[90mCommon commands: info block, info cpus, info status, info network\x1b[0m');
          term.writeln('');
          writePrompt();
          break;

        case 'response':
          // Display command output
          if (msg.output) {
            // Split output into lines and write each
            const lines = msg.output.split('\n');
            for (const line of lines) {
              // Don't write empty trailing line
              if (line || lines.indexOf(line) !== lines.length - 1) {
                term.writeln(line);
              }
            }
          }
          writePrompt();
          pendingCommand = null;
          break;

        case 'error':
          term.writeln(`\x1b[1;31mError: ${msg.message || 'Unknown error'}\x1b[0m`);
          writePrompt();
          pendingCommand = null;
          break;
      }
    } catch {
      term.writeln(`\x1b[1;31mError: Invalid response from server\x1b[0m`);
      writePrompt();
    }
  };

  monitorWebSocket.onerror = () => {
    term.writeln('');
    term.writeln('\x1b[1;31mWebSocket error. Connection failed.\x1b[0m');
    isConnected = false;
  };

  monitorWebSocket.onclose = (event) => {
    if (isConnected) {
      term.writeln('');
      term.writeln(`\x1b[1;33mConnection closed${event.reason ? `: ${event.reason}` : ''}\x1b[0m`);
    } else {
      term.writeln('');
      term.writeln('\x1b[1;31mFailed to connect to QEMU monitor.\x1b[0m');
      term.writeln('\x1b[90mMake sure the VM is running and has QMP enabled.\x1b[0m');
    }
    isConnected = false;
  };

  // Handle key input
  term.onKey(({ key, domEvent }) => {
    const code = domEvent.keyCode;

    // Don't process input if not connected or command pending
    if (!isConnected) {
      return;
    }

    if (code === 13) {
      // Enter
      term.writeln('');
      const cmd = commandBuffer.trim();
      commandBuffer = '';

      if (cmd) {
        commandHistory.push(cmd);
        historyIndex = commandHistory.length;

        // Handle local commands
        if (cmd === 'clear') {
          term.clear();
          writePrompt();
          return;
        }

        // Send command via WebSocket
        if (monitorWebSocket && monitorWebSocket.readyState === WebSocket.OPEN) {
          pendingCommand = cmd;
          monitorWebSocket.send(cmd);
        } else {
          term.writeln('\x1b[1;31mError: Not connected\x1b[0m');
          writePrompt();
        }
      } else {
        writePrompt();
      }
    } else if (code === 8) {
      // Backspace
      if (commandBuffer.length > 0 && !pendingCommand) {
        commandBuffer = commandBuffer.slice(0, -1);
        term.write('\b \b');
      }
    } else if (code === 38) {
      // Up arrow
      if (historyIndex > 0 && !pendingCommand) {
        clearLine();
        historyIndex--;
        commandBuffer = commandHistory[historyIndex];
        term.write(commandBuffer);
      }
    } else if (code === 40) {
      // Down arrow
      if (!pendingCommand) {
        clearLine();
        if (historyIndex < commandHistory.length - 1) {
          historyIndex++;
          commandBuffer = commandHistory[historyIndex];
          term.write(commandBuffer);
        } else {
          historyIndex = commandHistory.length;
          commandBuffer = '';
        }
      }
    } else if (code === 9) {
      // Tab - ignore
      domEvent.preventDefault();
    } else if (key.length === 1 && !domEvent.ctrlKey && !domEvent.altKey) {
      // Regular character
      if (!pendingCommand) {
        commandBuffer += key;
        term.write(key);
      }
    } else if (domEvent.ctrlKey && code === 67) {
      // Ctrl+C
      term.writeln('^C');
      commandBuffer = '';
      pendingCommand = null;
      writePrompt();
    } else if (domEvent.ctrlKey && code === 76) {
      // Ctrl+L
      term.clear();
      writePrompt();
    }
  });

  term.focus();

  // Paste handler — inserts clipboard text into the command buffer.
  // Defined here to capture commandBuffer/term/isConnected via closure.
  function monitorPasteHandler(): void {
    if (!isConnected || pendingCommand) return;

    pasteDialog((text) => {
      // Strip newlines — HMP commands are single-line
      const cleaned = text.replace(/[\r\n]+/g, '');
      commandBuffer += cleaned;
      term.write(cleaned);
    });
  }

  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).monitorPaste = monitorPasteHandler;
  }
}
